import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Container,
  IconButton,
  InputAdornment,
  LinearProgress,
  Link,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import VisibilityRoundedIcon from "@mui/icons-material/VisibilityRounded";
import VisibilityOffRoundedIcon from "@mui/icons-material/VisibilityOffRounded";
import { Link as RouterLink, useNavigate } from "react-router-dom";

const getPasswordStrength = (password) => {
  let score = 0;

  if (password.length >= 8) score += 25;
  if (/[A-Z]/.test(password)) score += 20;
  if (/[a-z]/.test(password)) score += 15;
  if (/\d/.test(password)) score += 20;
  if (/[^A-Za-z0-9]/.test(password)) score += 20;

  if (!password) {
    return { value: 0, label: "", color: "rgba(17,17,17,0.08)" };
  }

  if (score <= 35) {
    return { value: score, label: "Seguridad baja", color: "#e50914" };
  }

  if (score <= 65) {
    return { value: score, label: "Seguridad media", color: "#ff9800" };
  }

  if (score <= 85) {
    return { value: score, label: "Seguridad buena", color: "#22c55e" };
  }

  return { value: 100, label: "Seguridad alta", color: "#16a34a" };
};

export default function Register() {
  const navigate = useNavigate();

  const [form, setForm] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    password: "",
    confirmPassword: "",
  });

  const [errors, setErrors] = useState({});
  const [banner, setBanner] = useState({
    type: "",
    message: "",
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const passwordStrength = useMemo(
    () => getPasswordStrength(form.password),
    [form.password]
  );

  const passwordMatch =
    form.confirmPassword.length > 0 && form.password === form.confirmPassword;

  const passwordMismatch =
    form.confirmPassword.length > 0 && form.password !== form.confirmPassword;

  const handleChange = (field) => (event) => {
    const value = event.target.value;

    setForm((prev) => ({
      ...prev,
      [field]: value,
    }));

    setErrors((prev) => ({
      ...prev,
      [field]: "",
      general: "",
    }));

    if (banner.message) {
      setBanner({ type: "", message: "" });
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!form.firstName.trim()) newErrors.firstName = "Ingresa tu nombre";
    if (!form.lastName.trim()) newErrors.lastName = "Ingresa tu apellido";

    if (!form.email.trim()) {
      newErrors.email = "Ingresa tu correo electrónico";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) {
      newErrors.email = "Ingresa un correo válido";
    }

    if (!form.phone.trim()) {
      newErrors.phone = "Ingresa tu número de teléfono";
    }

    if (!form.password) {
      newErrors.password = "Ingresa una contraseña";
    } else if (form.password.length < 8) {
      newErrors.password = "La contraseña debe tener al menos 8 caracteres";
    }

    if (!form.confirmPassword) {
      newErrors.confirmPassword = "Confirma tu contraseña";
    } else if (form.password !== form.confirmPassword) {
      newErrors.confirmPassword = "Las contraseñas no coinciden";
    }

    setErrors(newErrors);

    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!validateForm()) return;

    setLoading(true);
    setBanner({ type: "", message: "" });

    try {
      const response = await fetch("/api/gym/register/", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          first_name: form.firstName.trim(),
          last_name: form.lastName.trim(),
          email: form.email.trim().toLowerCase(),
          phone: form.phone.trim(),
          password: form.password,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        const message = data.error || "No se pudo crear la cuenta";
        setErrors((prev) => ({
          ...prev,
          general: message,
        }));
        setBanner({
          type: "error",
          message,
        });
        return;
      }

      navigate("/verificar-cuenta", {
        state: {
          email: form.email.trim().toLowerCase(),
          message: data.message || "Te enviamos un código de verificación a tu correo.",
        },
      });
    } catch (error) {
      const message = "Ocurrió un error al conectar con el servidor";
      setErrors((prev) => ({
        ...prev,
        general: message,
      }));
      setBanner({
        type: "error",
        message,
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        width: "100%",
        minHeight: "100vh",
        bgcolor: "#ffffff",
        py: { xs: 5, md: 7 },
      }}
    >
      <Container
        maxWidth={false}
        sx={{ width: { xs: "90%", md: "75%" }, mx: "auto" }}
      >
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
            gap: { xs: 4, md: 4 },
            alignItems: "stretch",
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              pr: { md: 2 },
            }}
          >
            <Box
              sx={{
                display: "inline-flex",
                alignSelf: { xs: "center", md: "flex-start" },
                px: 1.5,
                py: 0.7,
                borderRadius: 999,
                bgcolor: "rgba(229,9,20,0.08)",
                border: "1px solid rgba(229,9,20,0.12)",
                mb: 1.8,
              }}
            >
              <Typography
                sx={{
                  color: "#e50914",
                  fontWeight: 800,
                  fontSize: { xs: 12.8, md: 13.2 },
                  letterSpacing: "0.02em",
                }}
              >
                REGISTRO NOVAXFIT
              </Typography>
            </Box>

            <Typography
              component="h1"
              sx={{
                color: "#111111",
                fontWeight: 900,
                lineHeight: 1.02,
                letterSpacing: -1.2,
                textAlign: { xs: "center", md: "left" },
                fontSize: {
                  xs: "clamp(34px, 8vw, 44px)",
                  md: "clamp(42px, 3.4vw, 58px)",
                },
                mb: 1.5,
              }}
            >
              Crea tu cuenta y empieza con NovaXFit
            </Typography>

            <Typography
              sx={{
                color: "rgba(17,17,17,0.72)",
                lineHeight: 1.8,
                fontSize: { xs: 15.5, md: 16.5 },
                fontWeight: 500,
                textAlign: { xs: "center", md: "left" },
                maxWidth: 560,
                mx: { xs: "auto", md: 0 },
              }}
            >
              Regístrate con tus datos para formar parte de la nueva experiencia
              digital de{" "}
              <Box
                component="strong"
                sx={{ fontWeight: 800, color: "#111111" }}
              >
                NovaXFit
              </Box>
              .
            </Typography>
          </Box>

          <Box
            component="form"
            onSubmit={handleSubmit}
            sx={{
              borderRadius: { xs: "24px", md: "28px" },
              border: "1px solid rgba(17,17,17,0.08)",
              bgcolor: "#ffffff",
              boxShadow: "0 24px 70px rgba(0,0,0,0.08)",
              p: { xs: 2.2, sm: 3, md: 3.2 },
            }}
          >
            <Stack spacing={2}>
              {banner.message && (
                <Alert severity={banner.type} sx={{ borderRadius: "14px" }}>
                  {banner.message}
                </Alert>
              )}

              <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                <TextField
                  fullWidth
                  label="Nombre"
                  value={form.firstName}
                  onChange={handleChange("firstName")}
                  error={Boolean(errors.firstName)}
                  helperText={errors.firstName || " "}
                  variant="outlined"
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "16px",
                    },
                  }}
                />

                <TextField
                  fullWidth
                  label="Apellido"
                  value={form.lastName}
                  onChange={handleChange("lastName")}
                  error={Boolean(errors.lastName)}
                  helperText={errors.lastName || " "}
                  variant="outlined"
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "16px",
                    },
                  }}
                />
              </Stack>

              <TextField
                fullWidth
                label="Correo electrónico"
                type="email"
                value={form.email}
                onChange={handleChange("email")}
                error={Boolean(errors.email)}
                helperText={errors.email || " "}
                variant="outlined"
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "16px",
                  },
                }}
              />

              <TextField
                fullWidth
                label="Número de teléfono"
                value={form.phone}
                onChange={handleChange("phone")}
                error={Boolean(errors.phone)}
                helperText={errors.phone || " "}
                variant="outlined"
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "16px",
                  },
                }}
              />

              <TextField
                fullWidth
                label="Contraseña"
                type={showPassword ? "text" : "password"}
                value={form.password}
                onChange={handleChange("password")}
                error={Boolean(errors.password)}
                helperText={errors.password || " "}
                variant="outlined"
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "16px",
                  },
                }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        edge="end"
                        onClick={() => setShowPassword((prev) => !prev)}
                        sx={{ color: "#111111" }}
                      >
                        {showPassword ? (
                          <VisibilityOffRoundedIcon />
                        ) : (
                          <VisibilityRoundedIcon />
                        )}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              <Box sx={{ mt: -0.5 }}>
                <LinearProgress
                  variant="determinate"
                  value={passwordStrength.value}
                  sx={{
                    height: 10,
                    borderRadius: 999,
                    bgcolor: "rgba(17,17,17,0.08)",
                    "& .MuiLinearProgress-bar": {
                      borderRadius: 999,
                      backgroundColor: passwordStrength.color,
                    },
                  }}
                />
                <Typography
                  sx={{
                    mt: 0.9,
                    fontSize: 13.5,
                    fontWeight: 700,
                    color:
                      passwordStrength.color === "rgba(17,17,17,0.08)"
                        ? "rgba(17,17,17,0.45)"
                        : passwordStrength.color,
                  }}
                >
                  {passwordStrength.label || "La contraseña debe ser segura"}
                </Typography>
              </Box>

              <TextField
                fullWidth
                label="Confirmar contraseña"
                type={showConfirmPassword ? "text" : "password"}
                value={form.confirmPassword}
                onChange={handleChange("confirmPassword")}
                error={Boolean(errors.confirmPassword) || passwordMismatch}
                helperText={
                  errors.confirmPassword
                    ? errors.confirmPassword
                    : passwordMismatch
                    ? "Las contraseñas no coinciden"
                    : passwordMatch
                    ? "Las contraseñas coinciden"
                    : " "
                }
                FormHelperTextProps={{
                  sx: {
                    color:
                      errors.confirmPassword || passwordMismatch
                        ? "#e50914"
                        : passwordMatch
                        ? "#16a34a"
                        : "transparent",
                    fontWeight: 600,
                  },
                }}
                variant="outlined"
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "16px",
                  },
                }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        edge="end"
                        onClick={() =>
                          setShowConfirmPassword((prev) => !prev)
                        }
                        sx={{ color: "#111111" }}
                      >
                        {showConfirmPassword ? (
                          <VisibilityOffRoundedIcon />
                        ) : (
                          <VisibilityRoundedIcon />
                        )}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              <Button
                type="submit"
                variant="contained"
                fullWidth
                disabled={loading}
                sx={{
                  mt: 0.5,
                  textTransform: "none",
                  fontWeight: 800,
                  borderRadius: "16px",
                  py: 1.25,
                  fontSize: "1rem",
                  color: "#ffffff",
                  background:
                    "linear-gradient(135deg, #5f0008 0%, #a80d19 35%, #df2632 62%, #161616 100%)",
                  boxShadow: "0 18px 50px rgba(229,9,20,0.22)",
                  border: "1px solid rgba(0,0,0,0.08)",
                  "&:hover": {
                    filter: "brightness(1.05)",
                    boxShadow: "0 22px 56px rgba(229,9,20,0.26)",
                  },
                  "&.Mui-disabled": {
                    color: "#ffffff",
                    opacity: 0.75,
                  },
                }}
              >
                {loading ? "Creando cuenta..." : "Crear cuenta"}
              </Button>

              <Typography
                sx={{
                  textAlign: "center",
                  color: "rgba(17,17,17,0.72)",
                  fontSize: 15,
                  fontWeight: 500,
                }}
              >
                ¿Ya tienes una cuenta?{" "}
                <Link
                  component={RouterLink}
                  to="/login"
                  underline="none"
                  sx={{
                    color: "#e50914",
                    fontWeight: 800,
                    "&:hover": {
                      color: "#b30010",
                    },
                  }}
                >
                  Iniciar sesión
                </Link>
              </Typography>
            </Stack>
          </Box>
        </Box>
      </Container>
    </Box>
  );
}